import React, {
  Fragment,
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  createContext,
  Dispatch,
  SetStateAction,
  forwardRef,
} from "react";
import cloneDeep from "lodash.clonedeep";
import {
  thirdServiceUsed,
  faceRecognition,
  base64ToBlob as base64ToBlobUtil,
  myFaceCut,
} from "@sunzi/utils";
import { Shop, IncreasePrice } from "@/sunzi";
import { Increment, VipIncrement } from "@/increment/increment";
import {
  i18n,
  compressImage,
  loadBase64FromURL,
  uploadKey,
  getToken7n,
  upload7nB64,
  slim7n,
  urlToBlob,
} from "@/utils";
import { ZuploaderLocal } from "@/plugin/zuploader";
import {
  newSockFBX,
  newBoxerFBX,
  newBoxer435FBX,
  newBoxer395ZFBX,
  newBoxer015ZFBX,
  newBoxerNormal,
  newBoxerDotted,
  newBoxerSewing,
  newSockNormal,
  newSockShadow,
  newKYBoxerFBX,
  newKYBoxerSewing,
  newBoxer435Sewing,
  newBoxer015ZSewing,
  newBoxer015ZDotted,
} from "@/config";
import { Layout, Loading, Clip, Header, Icon, Wrapper } from "@/components";
import { useLocal } from "@/hooks";
import {
  SockType,
  SockData,
  FaceRect,
  ProductType,
  ImageData,
  CacheImageData,
  ConfirmOutputImage,
  ICustomOutput,
  IConfirmOutput,
  IUpgradeVersionData,
} from "./new-sock";
import sockConfig from "./new-sock-config";
import Animation from "./animation";
import Pattern from "./pattern";
import Sock3d from "./sock3d";
import Error from "./error";
import Customize from "./customize";
import Edit from "./edit";
import NewDesign from "./new-design";
import styles from "./style.less";
import isBase64 from "@/utils/isbase64";

export enum SockTaskStatus {
  Input, // 上传图片
  NetworkError, // 网络错误
  CropError, // 裁剪错误
  Customize, // 定制页面
  Edit, // 编辑
  Crop, // 裁剪
}

export const ProductContext = createContext<{
  backgroundImage?: string;
  sockData: SockData[];
  allPrice: { price: number; originPrice: number };
  title?: string;
  shop: any;
  lineTextsIncrease: IncreasePrice | undefined;
  avatarIncrease: IncreasePrice | undefined;
  images: ImageData[];
  setImages: Dispatch<SetStateAction<ImageData[]>>;
  productSelected: ProductType;
  setProductSelected: Dispatch<SetStateAction<ProductType>>;
  setTaskStatus: Dispatch<SetStateAction<SockTaskStatus>>;
  setAllPrice: Dispatch<SetStateAction<{ price: number; originPrice: number }>>;
}>({
  sockData: [],
  allPrice: {
    price: 0,
    originPrice: 0,
  },
  shop: {},
  lineTextsIncrease: {
    price: 0,
    virtualId: 0,
  },
  avatarIncrease: {
    price: 0,
    virtualId: 0,
  },
  images: [],
  setImages: () => {},
  productSelected: {
    size: "",
    color: "",
  },
  setProductSelected: () => {},
  setTaskStatus: () => {},
  setAllPrice: () => {},
});

export interface NewSockHandle {
  setPrice: (price: number, originPrice: number) => void;
  setApendPrice: (price: number, originPrice: number) => void;
  setApendAlbum: (album: string) => void;
}

export interface NewSockProps {
  /** 产品类型 */
  type?: SockType;
  /** 店铺信息 */
  shop: Shop;
  /** 产品标题 */
  title: string;
  /** 价格 */
  price: number;
  /** 初始价格 */
  originPrice: number;
  /** 直线刻字加价 */
  lineTextsIncrease?: IncreasePrice;
  /** 头像加价 */
  avatarIncrease?: IncreasePrice;
  /** 增值服务 */
  increment?: Increment;
  /** 第二次设计 */
  doubleDesign?: boolean;
  /** 定制数据 */
  sockData: SockData[];
  /** 追加定制数据 */
  apendSockData?: SockData[];
  /** 用户选择的数据 */
  selectedData: ProductType;
  /** 3d区域默认背景图 */
  backgroundImage?: string;
  /** 分享链接 */
  shareLink?: string;
  /** 分享文案 */
  shareText?: string;
  /** 分享优惠价格 */
  sharePrice?: number;
  /** 分享优惠券图案 */
  shareCoupon?: string;
  /** 分享事件分类 */
  shareEventCategory?: string;
  /** 默认刻字文案 */
  defaultLineText?: string;
  /** 前缀文案 */
  prefixLineText?: string;
  /** 是否人脸识别 */
  isFaceFit?: boolean;
  /** 材质升级 */
  upgradeData?: IUpgradeVersionData;
  /** 变体切换 */
  onVariantChange?: (data: ProductType) => void;
  /** 追加产品变体切换 */
  onApendVariantChange?: (data: ProductType) => void;
  /** 关闭回调 */
  onClose: () => void;
  /** 确认回调 */
  onConfirm(
    output: ICustomOutput[],
    vipService?: VipIncrement,
    share?: boolean,
    isUpgradeVersion?: boolean,
  ): void;
  /** 谷歌分析 */
  gtag: any;
}

const NewSock: React.ForwardRefRenderFunction<NewSockHandle, NewSockProps> = (
  props,
  ref,
) => {
  const {
    type = SockType.SOCK,
    shop,
    title,
    price,
    originPrice,
    lineTextsIncrease,
    avatarIncrease,
    increment,
    sockData,
    apendSockData,
    selectedData,
    backgroundImage,
    shareLink,
    shareText,
    sharePrice,
    shareCoupon,
    shareEventCategory,
    defaultLineText,
    doubleDesign,
    prefixLineText,
    isFaceFit,
    onVariantChange,
    onApendVariantChange,
    onClose,
    onConfirm,
    gtag,
    upgradeData,
  } = props;

  // 设置国际化
  useLocal(shop.language);

  // 上一个任务 hooks
  const usePrevTaskState = (status: SockTaskStatus) => {
    const taskStatus = useRef<SockTaskStatus>();

    useEffect(() => {
      taskStatus.current = status;
    });

    return taskStatus.current;
  };

  /** 用户当前上传的图片 */
  const uploadingImage = useRef<string>("");
  /** 贴图生成类 */
  const _pattern = useRef<Pattern>();
  /** Sock3d 类 */
  const _sock3d = useRef<Sock3d>();
  /** clip 组件 */
  const _clip = useRef<Clip>(null);
  /** 底图 base64 缓存 */
  const _backgroundCache = useRef<CacheImageData>({});
  /** 第一次是否上传图片 */
  const _firstInput = useRef<boolean>(false);
  /** 动画类 */
  const _animation = useRef<Animation>();
  /** 任务状态 */
  const [taskStatus, setTaskStatus] = useState<SockTaskStatus>(
    SockTaskStatus.Input,
  );
  /** 上一个任务状态 */
  const prevTaskStatus = usePrevTaskState(taskStatus);
  /** 缓存更改监听 */
  const [loadCache, setLoadCache] = useState<boolean>(false);
  /** 插件 zuplaoder 显隐 */
  const [zuploaderVisible, setZuploaderVisible] = useState<boolean>(true);
  /** 用户选择袜子信息 */
  const [productSelected, setProductSelected] = useState<ProductType>({
    size: selectedData.size,
    color: selectedData.color,
  });
  /** 当前要处理的图片下标 */
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  /** 显示图片的集合 */
  const [images, setImages] = useState<ImageData[]>([]);
  /** 当前用户刻字 */
  const [currentLettering, setCurrentLettering] = useState<string>("");
  /** 图片裁剪状态 */
  const [cutState, setCutState] = useState<boolean>(false);
  /** 加车上传加载 */
  const [addCartLoading, setAddCartLoading] = useState<boolean>(false);
  /** 分享状态 */
  const [isShared, setIsShared] = useState<boolean>(false);
  /** 加载 */
  const [loading, setLoading] = useState<boolean>(false);
  /** 价格 */
  const [allPrice, setAllPrice] = useState<{
    price: number;
    originPrice: number;
  }>({
    price,
    originPrice,
  });
  /** 存储的字体数据 */
  const _saveFont = useRef<IConfirmOutput>({});
  /** 第二份输出数据 */
  const _newDesignOutput = useRef<ICustomOutput[]>([]);
  /** 新的设计 */
  const [newDesignVisible, setNewDesignVisible] = useState<boolean>(false);
  /** apend 组件 ref */
  const apendRef = useRef<any>(null);

  // 手动模拟暴露子组件函数
  const apendRefFunction = useCallback((ref) => {
    if (ref) {
      apendRef.current = ref();
    }
  }, []);

  useImperativeHandle(ref, () => ({
    setPrice: (price: number, originPrice: number) => {
      setAllPrice({
        price,
        originPrice,
      });
    },
    setApendPrice: (price: number, originPrice: number) => {
      apendRef.current.setPrice(price, originPrice);
    },
    setApendAlbum: (album: string) => apendRef.current.setAlbum(album),
  }));

  let fontInnerHTML = "";
  /** 字体模板 */
  const templateFontHtml = (item: any) => {
    const html = `
      @font-face {
        font-weight: normal;
        font-style: normal;
        font-family: ${item.name};
        src: url('${item.url}');
      }
    `;
    fontInnerHTML += html;
  };

  sockData.map((item) =>
    item.connects
      .filter((connect) => connect.lineTexts)
      .map((text) =>
        text.lineTexts?.map((a) => {
          templateFontHtml(a.font);
          a.fonts && a.fonts.map((font) => templateFontHtml(font));
        }),
      ),
  );

  /** 处理图片 */
  const dealImage = (
    newImages: string[],
    originImage: string,
    faceRects?: (FaceRect | undefined)[],
  ) => {
    let imagesTmp = cloneDeep(images);
    const newImagesTmp: ImageData[] = newImages.map((item, index) => {
      return {
        image: item,
        origin: originImage,
        faceRect: faceRects && faceRects[index],
      };
    });
    switch (prevTaskStatus) {
      case SockTaskStatus.NetworkError:
      case SockTaskStatus.CropError:
        // 如果上一个页面是错误页面，就是替换图片
        imagesTmp.splice(currentIndex, 1, ...newImagesTmp);
        break;
      case SockTaskStatus.Customize:
        // 如果上一个页面是定制页面，就是添加图片
        imagesTmp = imagesTmp.concat(newImagesTmp);
        setCurrentIndex(images.length - 1);
        break;
      case SockTaskStatus.Edit:
        // 如果上一个页面是编辑页面，就是替换图片
        imagesTmp.splice(currentIndex, 1, ...newImagesTmp);
        break;
      default:
        // 如果上一个页面为空，就是一开始进来页面
        imagesTmp = imagesTmp.concat(newImagesTmp);
        break;
    }
    setImages(cloneDeep(imagesTmp));
  };

  /** Zuploader 关闭 */
  const handleZuploadClose = () => {
    if (!_firstInput.current) {
      onClose();
    } else {
      setZuploaderVisible(false);
    }
  };

  /** Zuploader 确认回调 */
  const handleZuploadConfirm = async (output: string[]) => {
    if (!_firstInput.current) _firstInput.current = true;
    setZuploaderVisible(false);
    setCutState(false);
    const image = await compressImage(output[0], 800);
    // 因为图片可能上传失败，这里将插件上传的图片保存起来，以便重传
    uploadingImage.current = image;
    setLoading(true);
    requestCutFace(image);
  };

  // plugin: zuploader
  const zuploader = zuploaderVisible && (
    <ZuploaderLocal
      onClose={handleZuploadClose}
      onConfirm={handleZuploadConfirm}
    />
  );

  /**
   * 打开第二份设计
   * @param fontColor 字体颜色
   * @param font 字体
   */
  const handleNewDesignOpen = (output: IConfirmOutput) => {
    _saveFont.current = output;
    setNewDesignVisible(true);
  };

  /** 第二份设计确认回调 */
  const handleNewDesignConfirm = (
    output: ICustomOutput[],
    vipService?: VipIncrement,
  ) => {
    _newDesignOutput.current.push(...cloneDeep(output));
    handleConfirm({
      ..._saveFont.current,
      data: { ..._saveFont.current.data, vip: vipService },
    });
    setNewDesignVisible(false);
  };

  /** 确认加车 */
  const handleConfirm = async (
    output: IConfirmOutput,
    apendIds?: { sizeId: string; colorId: string },
  ) => {
    const { fontColor, font, data, isUpgradeVersion } = output;
    try {
      if (
        productSelected.size !== undefined &&
        productSelected.color !== undefined &&
        _pattern.current
      ) {
        gtag("event", "event_add_cart");
        const meshPreviewSource = _sock3d.current!.exportImage();
        // 加载
        setAddCartLoading(true);
        // 获取token
        const [token, meshPreview] = await Promise.all([
          getToken7n(),
          compressImage(meshPreviewSource, 300, "png"),
        ]);
        const encodeKey = `${uploadKey("new-sock")}`;
        const results: ConfirmOutputImage[] = [];
        const meshPreviewRequest = upload7nB64({
          base64: meshPreview,
          token,
          key: `${encodeKey}-preview.png`,
        });
        // 上传裁剪图
        const userImageRequest = Promise.all(
          images.map(async (item: ImageData, index: number) => {
            // 当前最大的头像
            const maxCount = _pattern.current!.getImageCount();
            if (maxCount > index) {
              // XXX: 新方法扣头返回的已经是链接 无需上传
              const [/* response,  */ originResponse] = await Promise.all([
                // upload7nB64({
                //   base64: item.image,
                //   token,
                //   key: `${encodeKey}-facut-${index}.png`,
                // }),
                upload7nB64({
                  base64: item.origin,
                  token,
                  key: `${encodeKey}-origin-${index}.png`,
                }),
              ]);
              let avatar = item.image;

              // 如果为手动裁剪返回base64，会替换item.image 需要手动上传
              if (isBase64(item.image)) {
                const response = await upload7nB64({
                  base64: item.image,
                  token,
                  key: `${encodeKey}-facut-${index}.png`,
                });
                avatar = host7n["prefix-na0"] + response.key;
              }

              results.push({
                origin: host7n["prefix-na0"] + originResponse.key,
                // avatar: host7n["prefix-na0"] + response.key,
                avatar,
              });
            }
          }),
        );
        const [meshPreviewResult] = await Promise.all([
          meshPreviewRequest,
          userImageRequest,
        ]);
        const meshPreviewUrl = host7n["prefix-na0"] + meshPreviewResult.key;

        _pattern.current.options[
          productSelected.size
        ].textVersion?.lineTexts?.map((item: any) => {
          item.fontColor = fontColor || item.fontColor;
          item.value = currentLettering;
          if (defaultLineText && prefixLineText) {
            // 如果有默认刻字的情况下，就组合
            item.value = `${prefixLineText} ${currentLettering}`;
          }
          item.font = font || item.font;
        });

        let connects: any = _pattern.current.isLettering
          ? _pattern.current.options[productSelected.size].textVersion
          : _pattern.current.options[productSelected.size].imageVersion;
        if (isFaceFit) {
          connects.images = _pattern.current.avatarData?.images;
        }
        const { id, name, value, imageUrl } =
          _backgroundCache.current[productSelected.size][productSelected.color];

        const outputData: any = {
          source: results,
          data: {
            connects: connects,
            bottomImage: {
              id,
              value,
              name,
              image: imageUrl,
            },
            size: {
              id: productSelected.size,
              value: _backgroundCache.current[productSelected.size].value,
              name: _backgroundCache.current[productSelected.size].size,
            },
            preview: meshPreviewUrl,
          },
          related: data?.relatedProduct,
        };

        if (data?.doubleDesign === false) {
          _newDesignOutput.current.push(outputData);
        }

        if (apendIds && apendSockData) {
          const apendData = cloneDeep(outputData);

          const currentSize = apendSockData.find(
            (item) => item.id == apendIds.sizeId,
          )!;
          const connectsData = currentSize.connects;
          let apendConnects = connectsData.find((item) => !item.lineTexts);
          if (outputData.data.connects.lineTexts) {
            connectsData.forEach((item) => {
              if (item.lineTexts) {
                apendConnects = item;
                apendConnects.lineTexts =
                  outputData.data.connects.lineTexts.value;
              }
            });
          }
          apendData.data.connects = apendConnects;
          apendData.data.bottomImage = currentSize.bottomImages.find(
            (item) => item.id == apendIds.colorId,
          )!;
          apendData.data.size = {
            id: currentSize.id,
            value: currentSize.value,
            name: currentSize.size,
          };
          delete apendData.data.preview;
          _newDesignOutput.current.push(apendData);
        }
        onConfirm(
          [outputData, ..._newDesignOutput.current],
          data?.vip,
          isShared,
          isUpgradeVersion,
        );
        setAddCartLoading(false);
        setTaskStatus(SockTaskStatus.Input);
        _firstInput.current = false;
        _newDesignOutput.current = [];
        setZuploaderVisible(true);
        setIsShared(false);
        setImages([]);
        // 如果有默认文案就设置刻字
        if (defaultLineText) {
          setCurrentLettering(defaultLineText);
        } else {
          setCurrentLettering("");
          _pattern.current.isLettering = false;
        }
        gtag("event", "event_add_cart_finish");
      }
    } catch (error) {
      alert(i18n.format("modules.new.sock.upload.error"));
      setAddCartLoading(false);
    }
  };

  /** 缓存图片 */
  const cacheBackgroundImage = async () => {
    let cache: CacheImageData = {};
    // 并行加载图片
    const _bottomImages: any = {};
    await Promise.all(
      sockData
        .map((item) => item.bottomImages)
        .reduce((a, b) => a.concat(b))
        .map((item) =>
          loadBase64FromURL(slim7n(item.image)).then((base64) => {
            _bottomImages[item.image] = base64;
          }),
        ),
    );
    // 构建缓存数据
    sockData.forEach(({ id, size, value, bottomImages }) => {
      cache[id] = { size, value };
      bottomImages.forEach((item) => {
        cache[id][item.id] = {
          id: item.id,
          value: item.value,
          name: item.name,
          image: _bottomImages[item.image],
          imageUrl: item.image,
          variantImage: item.variantImage,
          fontColors: item.fontColors,
        };
      });
    });
    _backgroundCache.current = cache;
  };

  /** 请求人脸分割 */
  const requestCutFace = async (base64: string, isReload: boolean = false) => {
    gtag("event", "event_start_crop");
    const stime = new Date().getTime();
    const blob = base64ToBlobUtil(base64);
    try {
      const response = await myFaceCut(blob);
      gtag("event", "event_end_crop");
      gtag("event", "timing_complete", {
        name: "event_crop_time",
        value: new Date().getTime() - stime,
      });
      let faceRects: (FaceRect | undefined)[] | undefined;
      if (isFaceFit) {
        faceRects = await Promise.all(
          response.map(async (item) => {
            const blobImage = await urlToBlob(item);
            try {
              const faceData = await faceRecognition(blobImage);
              if (faceData && faceData.length > 0) {
                return faceData[0].rect;
              } else {
                return undefined;
              }
            } catch (error) {
              return undefined;
            } finally {
              thirdServiceUsed({
                type: 5,
                source: 4,
                internalSource: 3,
              });
            }
          }),
        );
      }
      if (isReload) {
        response.forEach((item, index) => {
          images.push({
            image: item,
            origin: base64,
            faceRect: faceRects && faceRects[index],
          });
        });
        setImages(cloneDeep(images));
      } else {
        dealImage(response, base64, faceRects);
      }
    } catch (error: any) {
      console.log(error);
      switch (error.message) {
        case "RequestHandleError":
          // 切图服务错误
          setTaskStatus(SockTaskStatus.CropError);
        default:
          // 网络错误
          setTaskStatus(SockTaskStatus.NetworkError);
      }
      setLoading(false);
    } finally {
      thirdServiceUsed({
        type: 1,
        source: 4,
        internalSource: 3,
      });
    }
  };

  /** 请求重新上传 */
  const handleReUpload = () => {
    setLoading(true);
    requestCutFace(uploadingImage.current, true);
  };

  /** 手动裁剪完成 */
  const handleClipConfirm = async () => {
    const result = await _clip.current?.toClipURL();
    if (result) {
      if (prevTaskStatus === SockTaskStatus.Edit)
        images[currentIndex].image = result;
      else images.push({ image: result, origin: uploadingImage.current });
      setImages(cloneDeep(images));
      setTaskStatus(SockTaskStatus.Customize);
    }
  };

  /** 裁剪动画完成 */
  const faceCutEnd = () => {
    setLoading(false);
    const isInitial =
      Math.round(_sock3d.current!.camera.position.x) ===
      sockConfig[type].cameraPosition[0];
    const initialPosition = sockConfig[type].initialCamera;
    if (isInitial && _animation.current)
      _animation.current.initial(initialPosition, 2500);
  };

  /** 产品初始化 */
  const productInit = async () => {
    _pattern.current = new Pattern(
      sockData,
      {
        bottomImage: productSelected.color ?? sockData[0].bottomImages[0].id,
        size: productSelected.size ?? sockData[0].id,
      },
      !!defaultLineText,
    );

    let requestQueue: any[] = [
      _pattern.current.cacheImage(),
      _pattern.current.cacheFonts(),
      cacheBackgroundImage(),
    ];

    switch (type) {
      case SockType.SOCK:
        _sock3d.current = new Sock3d(
          {
            objectNames: [
              "polySurface1",
              "polySurface3",
              "polySurface4",
              "polySurface6",
              "polySurface7",
              "polySurface10",
              "polySurface11",
              "polySurface12",
              "polySurface17",
              "polySurface19",
              "polySurface20",
              "polySurface22",
              "polySurface23",
              "polySurface26",
              "polySurface27",
              "polySurface28",
            ],
            colorNames: [
              {
                name: "modelsPattern2D_22575",
                color: 0x0f0f0f,
              },
              {
                name: "polySurface24",
                color: 0xffffff,
              },
            ],
            cameraPosition: sockConfig[type].cameraPosition,
            ambientLightOpacity: 0,
          },
          type,
        );
        requestQueue = requestQueue.concat([
          _sock3d.current.loadFBX(newSockFBX),
          _sock3d.current.loadTexture(newSockNormal, "normal"),
          _sock3d.current.loadTexture(newSockShadow, "shadow"),
        ]);
        break;
      case SockType.BOXER:
      case SockType.BOXER015Z:
        _sock3d.current = new Sock3d(
          {
            objectNames: [
              "外_03",
              "顶_03",
              "缝纫线",
              "左边线",
              "右边线",
              "顶_后",
            ],
            colorNames: [
              {
                name: "内_03",
                color: 0xffffff,
              },
            ],
            cameraPosition: sockConfig[type].cameraPosition,
            ambientLightOpacity: 0,
          },
          type,
        );
        if (type === SockType.BOXER) {
          requestQueue = requestQueue.concat([
            _sock3d.current.loadFBX(newBoxerFBX),
            _sock3d.current.loadTexture(newBoxerSewing, "annex", "sewing"),
            _sock3d.current.loadTexture(newBoxerDotted, "annex", "dotted"),
          ]);
        } else if (type === SockType.BOXER015Z) {
          requestQueue = requestQueue.concat([
            _sock3d.current.loadFBX(newBoxer015ZFBX),
            _sock3d.current.loadTexture(newBoxer015ZSewing, "annex", "sewing"),
            _sock3d.current.loadTexture(newBoxer015ZDotted, "annex", "dotted"),
          ]);
        }
        requestQueue = requestQueue.concat([
          // _sock3d.current.loadFBX(newBoxerFBX),
          _sock3d.current.loadTexture(newBoxerNormal, "normal"),
          // _sock3d.current.loadTexture(newBoxerSewing, "annex", "sewing"),
          // _sock3d.current.loadTexture(newBoxerDotted, "annex", "dotted"),
        ]);
        break;
      case SockType.BOXER435:
      case SockType.BOXER395Z:
        _sock3d.current = new Sock3d(
          {
            objectNames: ["xian1", "tiemoban"],
            colorNames: [
              {
                name: "neili",
                color: 0xffffff,
              },
            ],
            cameraPosition: sockConfig[type].cameraPosition,
            ambientLightOpacity: 0,
          },
          type,
        );
        if (type === SockType.BOXER435) {
          requestQueue.push(_sock3d.current.loadFBX(newBoxer435FBX));
        } else if (type === SockType.BOXER395Z) {
          requestQueue.push(_sock3d.current.loadFBX(newBoxer395ZFBX));
        }
        requestQueue = requestQueue.concat([
          // _sock3d.current.loadFBX(newBoxer435FBX),
          _sock3d.current.loadTexture(newBoxerNormal, "normal"),
          _sock3d.current.loadTexture(newBoxer435Sewing, "annex", "sewing"),
        ]);
        break;
      case SockType.KYBOXER:
        _sock3d.current = new Sock3d(
          {
            objectNames: ["外", "顶部", "外_2", "顶部_后"],
            colorNames: [
              {
                name: "内部",
                color: 0xffffff,
              },
            ],
            cameraPosition: sockConfig[type].cameraPosition,
            ambientLightOpacity: 0,
          },
          type,
        );
        requestQueue = requestQueue.concat([
          _sock3d.current.loadFBX(newKYBoxerFBX),
          _sock3d.current.loadTexture(newBoxerNormal, "normal"),
          _sock3d.current.loadTexture(newKYBoxerSewing, "annex", "sewing"),
        ]);
        break;
    }
    await Promise.all(requestQueue);
    // 初始化动画类
    _animation.current = new Animation(
      {
        camera: _sock3d.current.camera,
        object: _sock3d.current.object,
        orbitControls: _sock3d.current.orbitControls,
      },
      type,
    );
    // 如果有默认文案就设置刻字
    if (defaultLineText) {
      setCurrentLettering(defaultLineText);
    }
    setLoadCache(true);
  };

  useEffect(() => {
    setProductSelected({
      color: selectedData.color,
      size: selectedData.size,
    });
  }, [selectedData.color, selectedData.size]);

  useEffect(() => {
    onVariantChange && onVariantChange(productSelected);
  }, [productSelected]);

  useEffect(() => {
    setTimeout(() => productInit(), 500);
  }, []);

  useEffect(() => {
    if (images.length > 0 && loadCache) {
      setTaskStatus(SockTaskStatus.Customize);
    }
  }, [images, loadCache]);

  /** 渲染页面 */
  const renderContent = () => {
    switch (taskStatus) {
      case SockTaskStatus.Input:
        return <Fragment />;
      case SockTaskStatus.NetworkError:
      case SockTaskStatus.CropError:
        return (
          <Error
            status={taskStatus}
            onClose={onClose}
            setZuploaderVisible={setZuploaderVisible}
            reUpload={handleReUpload}
          />
        );
      case SockTaskStatus.Customize:
      case SockTaskStatus.Edit:
      case SockTaskStatus.Crop:
        return (
          <Customize
            type={type}
            sock3d={_sock3d}
            pattern={_pattern}
            animation={_animation}
            doubleDesign={doubleDesign}
            prevTaskStatus={prevTaskStatus}
            onClose={onClose}
            backgroundCache={_backgroundCache}
            increment={increment}
            currentLettering={currentLettering}
            shareLink={shareLink}
            shareText={shareText}
            sharePrice={sharePrice}
            shareCoupon={shareCoupon}
            shareEventCategory={shareEventCategory}
            isShared={isShared}
            defaultLineText={defaultLineText}
            prefixLineText={prefixLineText}
            setCurrentLettering={setCurrentLettering}
            setCurrentIndex={setCurrentIndex}
            setZuploaderVisible={setZuploaderVisible}
            setCutState={setCutState}
            setIsShared={setIsShared}
            // setApendRef={setApendRef}
            onConfirm={handleConfirm}
            addCartLoading={addCartLoading}
            gtag={gtag}
            onNewDesign={handleNewDesignOpen}
            apendRefFunction={apendRefFunction}
            apendSockData={apendSockData}
            onApendVariantChange={onApendVariantChange}
            upgradeData={upgradeData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ProductContext.Provider
        value={{
          backgroundImage,
          sockData,
          allPrice,
          title,
          shop,
          lineTextsIncrease,
          avatarIncrease,
          images,
          setImages,
          productSelected,
          setProductSelected,
          setTaskStatus,
          setAllPrice,
        }}
      >
        <Layout plugins={[zuploader]}>
          <style>{fontInnerHTML}</style>
          <div className="sunzi-new-sock">
            {renderContent()}
            {taskStatus === SockTaskStatus.Edit && (
              <Wrapper className={styles.editWrapper}>
                <Edit
                  currentIndex={currentIndex}
                  onClose={() => setTaskStatus(SockTaskStatus.Customize)}
                  onConfirm={() => setTaskStatus(SockTaskStatus.Customize)}
                  setZuploaderVisible={setZuploaderVisible}
                  gtag={gtag}
                />
              </Wrapper>
            )}
            {taskStatus === SockTaskStatus.Crop && (
              <Wrapper className={styles.clipWrapper}>
                <Clip
                  ref={_clip}
                  source={
                    prevTaskStatus === SockTaskStatus.NetworkError ||
                    prevTaskStatus === SockTaskStatus.CropError
                      ? uploadingImage.current
                      : images[currentIndex].origin
                  }
                  gtag={gtag}
                  theme="light"
                  renderHeader={
                    <Header
                      closeIconType="left"
                      closeIconColor="#000"
                      onClose={() => {
                        switch (prevTaskStatus) {
                          case SockTaskStatus.Edit:
                            setTaskStatus(SockTaskStatus.Edit);
                            break;
                          case SockTaskStatus.CropError:
                            setTaskStatus(SockTaskStatus.CropError);
                            break;
                          case SockTaskStatus.NetworkError:
                            setTaskStatus(SockTaskStatus.NetworkError);
                            break;
                          default:
                            break;
                        }
                      }}
                      renderContent={
                        <div
                          className={styles.clipHeaderContent}
                          onClick={() => {
                            _clip.current?.onGuideVisible(true)();
                          }}
                        >
                          <Icon type="question" size={30} />
                        </div>
                      }
                      renderSubmit={
                        <div
                          className={styles.clipSubmit}
                          onClick={handleClipConfirm}
                        >
                          <span>{i18n.format("modules.global.done")}</span>
                        </div>
                      }
                    />
                  }
                />
              </Wrapper>
            )}
            {loading && (
              <Wrapper className={styles.loadingWrapper}>
                <Loading.Facut cutState={cutState} doneCallback={faceCutEnd} />
              </Wrapper>
            )}
          </div>
        </Layout>
      </ProductContext.Provider>
      {newDesignVisible && (
        <NewDesign
          {...props}
          increment={{ ...props.increment, doubleDesign: undefined }}
          doubleDesign={true}
          onClose={() => setNewDesignVisible(false)}
          onConfirm={handleNewDesignConfirm}
        />
      )}
    </>
  );
};

export default forwardRef(NewSock);
